import React, { Fragment, useEffect } from "react";
import PropTypes from "prop-types";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  FilterFn,
  flexRender,
  PaginationState,
} from "@tanstack/react-table";

//Import Flatepicker
import Flatpickr from "react-flatpickr";

import {
  Table,
  Row,
  Col,
  Button,
  InputGroup,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";
import { Link } from "react-router-dom";
import useDebounce from "@/utils/hooks/useDebounce";

interface GlobalFilterProps {
  //preGlobalFilteredRows: any;
  globalFilter?: any;
  setGlobalFilter: any;
}

const normalizeSearchValue = (value: unknown) =>
  String(value ?? "").toLowerCase();

// Define a default UI for filtering
function GlobalFilter({
  globalFilter,
  setGlobalFilter,
  setPagination, // Receive setPagination from parent
}: GlobalFilterProps & {
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
}) {
  const [value, setValue] = React.useState(normalizeSearchValue(globalFilter));
  const debouncedValue = useDebounce(value, 500);

  // Effect to update the parent's global filter and reset pagination
  // only when the debouncedValue changes (user stopped typing)
  useEffect(() => {
    // Set the global filter in the parent component
    setGlobalFilter(normalizeSearchValue(debouncedValue) || undefined);

    // Reset pagination to the first page when the debounced filter value changes
    setPagination((prevState) => ({
      ...prevState,
      pageIndex: 0,
    }));
  }, [debouncedValue, setGlobalFilter, setPagination]); // Depend on debouncedValue, setGlobalFilter, and setPagination

  // Update the local value when the parent's globalFilter prop changes (e.g., when the table is initially loaded with a filter)
  useEffect(() => {
    const nextValue = normalizeSearchValue(globalFilter);
    if (nextValue !== value) {
      // Prevent unnecessary updates if local value is already in sync
      setValue(nextValue);
    }
  }, [globalFilter]); // Depend on globalFilter

  const onChange = (e: any) => {
    // Update the local state immediately as the user types
    setValue(normalizeSearchValue(e.target.value));
    // The API call and pagination reset will happen in the useEffect when debouncedValue changes
  };

  return (
    <div className="d-inline-block">
      <input
        onChange={(e) => onChange(e)}
        id="search-bar-0"
        type="text"
        className="form-control font-size-13"
        value={value || ""}
        aria-label="Search this table"
      />
    </div>
  );
}

interface TableContainerProps {
  columns: ColumnDef<any>[];
  data: any;
  isGlobalFilter?: any;
  isAddOptions?: any;
  isAddUserList?: any;
  isAddInvoiceList?: any;
  handleOrderClicks?: any;
  handleUserClick?: any;
  handleCustomerClick?: any;
  isAddCustList?: any;
  className?: any;
  handleInvoiceClick?: any;
  theadClass?: any;
  isBordered?: boolean;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  pageCount: number;
  totalCount: number; // Add totalCount to display total records
  globalFilter: string; // Add globalFilter prop
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>; // Add setGlobalFilter prop
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>; // Add setSorting prop
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>; // Add setColumnFilters prop
  sorting?: SortingState;
  columnFilters?: ColumnFiltersState;
  rightToolbarContent?: React.ReactNode;
  // false: clicking a sorted header only flips asc/desc instead of clearing the sort.
  enableSortingRemoval?: boolean;
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isAddOptions,
  isAddInvoiceList,
  handleOrderClicks,
  handleUserClick,
  handleCustomerClick,
  isAddCustList,
  className,
  handleInvoiceClick,
  theadClass,
  isBordered,
  pagination,
  setPagination,
  pageCount,
  totalCount,
  globalFilter, // Destructure globalFilter prop
  setGlobalFilter, // Destructure setGlobalFilter prop
  setSorting, // Destructure setSorting prop
  setColumnFilters, // Destructure setColumnFilters prop
  sorting = [],
  columnFilters = [],
  rightToolbarContent,
  enableSortingRemoval,
}: TableContainerProps) => {
  // const [sorting, setSorting] = React.useState<SortingState>([]);
  // const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  // const [globalFilter, setGlobalFilter] = React.useState("");
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((prevState) => ({
        ...prevState,
        pageIndex: 0,
      }));
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    // getFilteredRowModel: getFilteredRowModel(),
    // getPaginationRowModel: getPaginationRowModel(), // Removed client-side pagination
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    defaultColumn: {
      cell: (info) => info.getValue(),
      header: (info) => info.column.id,
    },
    manualFiltering: true, // Indicate that filtering is handled manually
    manualSorting: true, // Indicate that sorting is handled manually
    ...(enableSortingRemoval === undefined ? {} : { enableSortingRemoval }),
    manualPagination: true, // Indicate that pagination is handled manually
    pageCount: pageCount, // Provide the total page count
  });

  const generateSortingIndicator = (column: any) => {
    // This can still be used to display the sorting direction icon in the header
    // The actual sorting logic is handled by the parent and backend
    const sorted = table.getState().sorting.find((s) => s.id === column.id);
    return sorted ? (
      sorted.desc ? (
        <span>&#9650;</span>
      ) : (
        <span>&#9660;</span>
      )
    ) : (
      ""
    );
  };

  // const onChangeInSelect = (event: any) => {
  //   table.setPageSize(Number(event.target.value));
  // };
  const onChangeInSelect = (event: any) => {
    setPagination((prevState) => ({
      ...prevState,
      pageSize: Number(event.target.value),
    }));
  };

  // const onChangeInInput = (event: any) => {
  //   const page = event.target.value ? Number(event.target.value) - 1 : 0;
  //   table.setPageIndex(page);
  // };
  const onChangeInInput = (event: any) => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    setPagination((prevState) => ({
      ...prevState,
      pageIndex: page,
    }));
  };

  const paginationLinks = (pageCount: number, currentPage: number) => {
    const pageNumbersToShow = 1;

    const startPage = Math.max(0, currentPage - pageNumbersToShow);
    const endPage = Math.min(pageCount - 1, currentPage + pageNumbersToShow);

    const pages = [];

    if (startPage > 0) {
      pages.push(0);
      if (startPage > 1) {
        pages.push("ellipsis");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < pageCount - 1) {
      if (endPage < pageCount - 2) {
        pages.push("ellipsis");
      }
      pages.push(pageCount - 1);
    }

    return (
      <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
        <li
          className={
            !table.getCanPreviousPage() ? "page-item disabled" : "page-item"
          }
        >
          <Link
            to="#"
            className="page-link"
            onClick={(e) => {
              e.preventDefault();
              table.previousPage();
            }}
          >
            Anterior
          </Link>
        </li>
        {/* {Array.from({ length: pageCount }).map((_, index) => (
              <React.Fragment key={index}>
                <li className="page-item">
                  <Link
                    to="#"
                    className={
                      table.getState().pagination.pageIndex === index
                        ? "page-link active"
                        : "page-link"
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      table.setPageIndex(index);
                    }}
                  >
                    {index + 1}
                  </Link>
                </li>
              </React.Fragment>
            ))} */}
        {pages.map((page, index) => (
          <React.Fragment key={index}>
            {page === "ellipsis" ? (
              <li className="page-item">
                <span className="page-link">...</span>
              </li>
            ) : (
              <li className="page-item">
                <Link
                  to="#"
                  className={
                    table.getState().pagination.pageIndex === page
                      ? "page-link active"
                      : "page-link"
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    table.setPageIndex(Number(page));
                  }}
                >
                  {Number(page) + 1}
                </Link>
              </li>
            )}
          </React.Fragment>
        ))}
        <li
          className={
            !table.getCanNextPage() ? "page-item disabled" : "page-item"
          }
        >
          <Link
            to="#"
            className="page-link"
            onClick={(e) => {
              e.preventDefault();
              table.nextPage();
            }}
          >
            Siguiente
          </Link>
        </li>
      </ul>
    );
  };

  return (
    <Fragment>
      <>
        {isAddInvoiceList && (
          <Row>
            <div className="col-sm">
              <div className="mb-4">
                <button
                  onClick={handleInvoiceClick}
                  type="button"
                  className="btn btn-light waves-effect waves-light"
                >
                  <i className="bx bx-plus me-1"></i> Add Invoice
                </button>
              </div>
            </div>
            <div className="col-sm-auto">
              <div className="d-flex align-items-center gap-1 mb-4">
                <InputGroup className="datepicker-range">
                  <Flatpickr
                    className="form-control d-block"
                    options={{
                      altInput: true,
                      altFormat: "F j, Y",
                      dateFormat: "Y-m-d",
                    }}
                  />
                  <button className="input-group-text" id="date1">
                    <i className="bx bx-calendar-event"></i>
                  </button>
                </InputGroup>
                <UncontrolledDropdown>
                  <DropdownToggle
                    className="btn btn-link text-muted py-1 font-size-16 shadow-none dropdown-toggle"
                    tag="a"
                  >
                    <i className="bx bx-dots-horizontal-rounded"></i>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem>Action</DropdownItem>
                    <DropdownItem>Another action</DropdownItem>
                    <DropdownItem>Something else here</DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
          </Row>
        )}
        <Row>
          <Col sm={12} md={rightToolbarContent ? 4 : 6}>
            <label className="d-flex">
              <span className="pt-1 fw-normal">Mostrar</span>
              <Col lg={1} className="ms-1 me-1" style={{ minWidth: "65px" }}>
                <select
                  className="custom-select me-2 pe-3 custom-select-sm form-control form-control-sm form-select form-select-sm"
                  value={table.getState().pagination.pageSize}
                  onChange={onChangeInSelect}
                >
                  {[50, 75, 100, 150].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {" "}
                      {pageSize}
                    </option>
                  ))}
                </select>
              </Col>{" "}
              <span className="pt-1 fw-normal">registros</span>
            </label>
          </Col>
          {(rightToolbarContent || isGlobalFilter) && (
            <Col sm={12} md={rightToolbarContent ? 8 : 6}>
              <div className="d-flex align-items-center justify-content-end gap-2 flex-nowrap">
                {rightToolbarContent}
                {isGlobalFilter && (
                  <label className="d-flex align-items-center fw-normal mb-0">
                    <span className="me-2">Buscar:</span>{" "}
                    <GlobalFilter
                      globalFilter={globalFilter} // Pass globalFilter prop
                      setGlobalFilter={setGlobalFilter} // Pass setGlobalFilter prop
                      setPagination={setPagination} // Pass setPagination prop
                    />
                  </label>
                )}
              </div>
            </Col>
          )}
        </Row>
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="12">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </>

      <div className="table-responsive">
        <Table
          hover
          {...{
            role: "table",
          }}
          className={className}
          bordered={isBordered}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {/* generateSortingIndicator still works for displaying icons */}
                      {generateSortingIndicator(header.column)}
                    </div>
                    {/* You would implement server-side column filters here if needed */}
                    {/* {header.column.getCanFilter() ? (
                      <div>
                        <Filter column={header.column} />
                      </div>
                    ) : null} */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center g-3 text-center text-sm-start">
        <div className="col-sm">
          <div>
            Mostrando{" "}
            <span className="fw-normal">
              {pagination.pageIndex * pagination.pageSize + 1} -{" "}
              {Math.min(
                (pagination.pageIndex + 1) * pagination.pageSize,
                totalCount,
              )}
            </span>{" "}
            de <span className="fw-normal">{totalCount}</span> resultados
          </div>
        </div>
        <div className="col-sm-auto">
          {/* <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
            <li
              className={
                !table.getCanPreviousPage() ? "page-item disabled" : "page-item"
              }
            >
              <Link
                to="#"
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
              >
                Anterior
              </Link>
            </li>
            {Array.from({ length: pageCount }).map((_, index) => (
              <React.Fragment key={index}>
                <li className="page-item">
                  <Link
                    to="#"
                    className={
                      table.getState().pagination.pageIndex === index
                        ? "page-link active"
                        : "page-link"
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      table.setPageIndex(index);
                    }}
                  >
                    {index + 1}
                  </Link>
                </li>
              </React.Fragment>
            ))}
            <li
              className={
                !table.getCanNextPage() ? "page-item disabled" : "page-item"
              }
            >
              <Link
                to="#"
                className="page-link"
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
              >
                Siguiente
              </Link>
            </li>
          </ul> */}
          {paginationLinks(pageCount, pagination.pageIndex)}
        </div>
      </Row>
    </Fragment>
  );
};

TableContainer.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  isGlobalFilter: PropTypes.bool,
  isAddOptions: PropTypes.bool,
  isAddUserList: PropTypes.bool,
  isAddInvoiceList: PropTypes.bool,
  handleOrderClicks: PropTypes.func,
  handleUserClick: PropTypes.func,
  handleCustomerClick: PropTypes.func,
  isAddCustList: PropTypes.bool,
  className: PropTypes.string,
  handleInvoiceClick: PropTypes.func,
  theadClass: PropTypes.string,
  isBordered: PropTypes.bool,
  pagination: PropTypes.object.isRequired,
  setPagination: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  globalFilter: PropTypes.string.isRequired,
  setGlobalFilter: PropTypes.func.isRequired,
  setSorting: PropTypes.func.isRequired, // Prop type for setSorting
  setColumnFilters: PropTypes.func.isRequired, // Prop type for setColumnFilters
  sorting: PropTypes.array,
  columnFilters: PropTypes.array,
  rightToolbarContent: PropTypes.node,
};

export default TableContainer;
